import { format, isPast, isToday, isTomorrow } from "date-fns";

interface TodoItemProps {
  todo: any;
  subjectName?: string | null;
  subjectColor?: string | null;
  subjectIcon?: string | null;
  onEdit: (todo: any) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, completed: boolean) => void;
  selectable?: boolean;
  selected?: boolean;
  onSelectToggle?: (id: string) => void;
  assigneeLabel?: string | null;
}

const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M9.5 2L11 3.5l-7 7H2.5V9l7-7z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M8 3.5l1.5 1.5" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M2 3.5h9M5 3.5V2.5h3v1M3.5 3.5l.5 7h5l.5-7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const priorityConfig: Record<string, { dot: string; badge: string; border: string }> = {
  high:   { dot: "bg-red-500",    badge: "bg-red-500/15 text-red-400",    border: "border-l-red-500" },
  medium: { dot: "bg-amber-500",  badge: "bg-amber-500/15 text-amber-400", border: "border-l-amber-500" },
  low:    { dot: "bg-green-500",  badge: "bg-green-500/15 text-green-400", border: "border-l-green-500" },
};

const recurrenceUnit: Record<string, string> = { daily: "day", weekly: "week", monthly: "month" };

const categoryColors: Record<string, string> = {
  work:     "bg-blue-500/15 text-blue-400",
  personal: "bg-purple-500/15 text-purple-400",
  shopping: "bg-pink-500/15 text-pink-400",
  health:   "bg-teal-500/15 text-teal-400",
  general:  "bg-border-strong text-muted",
};

export default function TodoItem({ todo, subjectName, subjectColor, subjectIcon, onEdit, onDelete, onToggle, selectable, selected, onSelectToggle, assigneeLabel }: TodoItemProps) {
  const pc = priorityConfig[todo.priority] || priorityConfig.medium;

  const getDateInfo = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isPast(d) && !isToday(d)) return { label: "Overdue", cls: "text-red-400 bg-red-500/10" };
    if (isToday(d)) return { label: "Today", cls: "text-amber-400 bg-amber-500/10" };
    if (isTomorrow(d)) return { label: "Tomorrow", cls: "text-blue-400 bg-blue-500/10" };
    return { label: format(d, "MMM d"), cls: "text-muted bg-surface-alt" };
  };

  const dateInfo = todo.dueDate ? getDateInfo(todo.dueDate) : null;

  return (
    <div
      className={`group relative flex items-start gap-2 p-2 sm:gap-2.5 sm:p-2.5 rounded-xl border-l-2 border border-border bg-surface hover:bg-surface-alt/70 hover:border-border-strong transition-all duration-150 ${pc.border} ${
        todo.completed ? "opacity-50" : ""
      }`}
    >
      {selectable && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onSelectToggle?.(todo._id)}
          className="mt-1 shrink-0 w-3.5 h-3.5 rounded accent-amber-500"
          aria-label="Select task"
        />
      )}

      {/* Checkbox */}
      <button
        onClick={() => onToggle(todo._id, !todo.completed)}
        className={`mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center transition-all ${
          todo.completed
            ? "bg-amber-500 border-amber-500"
            : "border-border-strong hover:border-amber-500"
        }`}
        aria-label="Toggle complete"
      >
        {todo.completed && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5l2 2 4-4" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1">
          <h3 className={`text-sm sm:text-base font-bold leading-snug ${todo.completed ? "line-through text-muted" : "text-text"}`}>
            {todo.title}
          </h3>

          {/* Actions - visible on hover */}
          <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
            <button
              onClick={() => onEdit(todo)}
              className="flex items-center gap-1 px-1.5 py-1 rounded-md text-xs font-semibold text-muted hover:text-amber-400 hover:bg-amber-500/10 border border-transparent hover:border-amber-500/20 transition-all"
            >
              <EditIcon />
              Edit
            </button>
            <button
              onClick={() => onDelete(todo._id)}
              className="flex items-center gap-1 px-1.5 py-1 rounded-md text-xs font-semibold text-muted hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all"
            >
              <DeleteIcon />
            </button>
          </div>
        </div>

        {todo.description && (
          <p className="text-xs text-muted mt-0.5 leading-relaxed font-medium line-clamp-2">{todo.description}</p>
        )}

        {/* Tags row */}
        <div className="flex flex-wrap items-center gap-1 mt-1">
          {/* Subject name */}
          {subjectName && (
            <span
              className="text-xs font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${subjectColor || "#f59e0b"}26`, color: subjectColor || "#f59e0b" }}
            >
              {subjectIcon ? `${subjectIcon} ` : ""}{subjectName}
            </span>
          )}

          {/* Priority */}
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded capitalize ${pc.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${pc.dot}`} />
            {todo.priority}
          </span>

          {/* Category */}
          {todo.category && todo.category !== "general" && (
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded capitalize ${categoryColors[todo.category] || categoryColors.general}`}>
              {todo.category}
            </span>
          )}

          {/* Assignee */}
          {assigneeLabel && (
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-border-strong text-text">
              @ {assigneeLabel}
            </span>
          )}

          {/* Due date */}
          {dateInfo && (
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${dateInfo.cls}`}>
              {dateInfo.label}
            </span>
          )}

          {/* Recurrence */}
          {todo.recurrence?.freq && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400" title={`Repeats every ${todo.recurrence.interval > 1 ? todo.recurrence.interval + " " : ""}${todo.recurrence.freq}`}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1.5 5a3.5 3.5 0 0 1 6-2.5M8.5 2v2h-2M8.5 5a3.5 3.5 0 0 1-6 2.5M1.5 8V6h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {todo.recurrence.interval > 1 ? `Every ${todo.recurrence.interval} ${recurrenceUnit[todo.recurrence.freq] || todo.recurrence.freq}s` : todo.recurrence.freq}
            </span>
          )}

          {/* Tags */}
          {todo.tags?.map((tag: string, idx: number) => (
            <span key={idx} className="text-xs font-medium px-1.5 py-0.5 rounded bg-surface-alt text-muted">
              #{tag}
            </span>
          ))}

          {/* Subtask progress */}
          {todo.subtasks?.length > 0 && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-surface-alt text-muted">
              {todo.subtasks.filter((s: any) => s.completed).length}/{todo.subtasks.length} done
            </span>
          )}

          {/* Attachment count */}
          {todo.attachments?.length > 0 && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-surface-alt text-muted">
              {todo.attachments.length} file{todo.attachments.length > 1 ? "s" : ""}
            </span>
          )}

          {/* Comment count */}
          {todo.comments?.filter((c: any) => c.type === "comment").length > 0 && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-surface-alt text-muted">
              💬 {todo.comments.filter((c: any) => c.type === "comment").length}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
